import { forwardRef } from 'react';
import type { ButtonHTMLAttributes } from 'react';
import { cx } from '../internal/cx';
import { devWarn } from '../internal/dev-warn';

/**
 * Quatro variantes, todas construídas para passar AA por padrão — a decisão de
 * contraste fica no componente, nunca no consumidor (mandato da rodada):
 *
 *  - `primary` — papel semântico `--button-primary*` (tokens.css), não hex fixo: no tema
 *    claro é Ink/Paper (14,98:1, seguro em qualquer tamanho); no ambiente autenticado
 *    ESCURO (`.argink-app-shell`/`.argink-theme-scope` com `data-theme="dark"`) os mesmos
 *    tokens resolvem para Trust Blue/branco, o par do baseline aprovado (R-UX02-P01/L3A-FIX,
 *    achado D-101/#9 — antes lia os primitivos fixos e ficava idêntico ao fundo no escuro).
 *  - `outline` — borda Blue (3,53:1 ≥ 3:1 de componente de UI), texto Ink sobre
 *    Paper (15,89:1). Ação secundária / "Blue como borda" (BRAND-EXTRACT §5).
 *  - `accent` — fundo Blue, texto Paper. Só passa AA em texto grande (3,53:1 ≥
 *    3:1); por isso `size="lg"` é obrigatório — usar em tamanho menor dispara aviso
 *    de dev e o componente força `lg` mesmo assim (nunca deixa a violação passar
 *    silenciosa).
 *  - `danger` — borda + texto `dangerText` (derivado, 4,59:1 sobre Paper — o
 *    vermelho de marca puro não passa em texto pequeno, ver tokens/colors.ts).
 */
export type ButtonVariant = 'primary' | 'outline' | 'accent' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Estado de carregamento — mantém o rótulo visível, anuncia via `aria-busy` +
   * texto para leitor de tela (nunca só um spinner mudo). */
  isLoading?: boolean;
  fullWidth?: boolean;
}

export interface ButtonAppearance {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  className?: string;
}

/**
 * As classes de um botão, sem o `<button>`.
 *
 * Existe porque **navegação não é botão**: os CTAs da Home levam a outra rota, e
 * um `<button onClick={navigate}>` perderia abrir-em-nova-aba, o menu de
 * contexto e o anúncio de "link" no leitor de tela. A alternativa seria a
 * aplicação escrever `argink-btn argink-btn--primary` à mão — passando a depender
 * de nomes de classe internos deste package, que é exatamente o acoplamento que
 * o contrato de dependência proíbe.
 *
 * Então o design system continua dono da aparência, e quem chama continua dono
 * do elemento. `Button` usa esta mesma função, para que as duas formas não
 * possam divergir.
 */
export function buttonClassName({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  className,
}: ButtonAppearance = {}): string {
  // Mesma proteção de contraste do componente: `accent` só passa AA em texto
  // grande, e isso não pode depender de quem monta o elemento.
  const effectiveSize = variant === 'accent' && size !== 'lg' ? 'lg' : size;
  if (variant === 'accent' && size !== 'lg') {
    devWarn(
      'Aparência de botão variant="accent" (fundo Trust Blue) só cumpre contraste AA em texto ' +
        'grande — size foi forçado para "lg" (BRAND-EXTRACT §5).',
    );
  }
  return cx(
    'argink-btn',
    `argink-btn--${variant}`,
    `argink-btn--${effectiveSize}`,
    fullWidth && 'argink-btn--full',
    'argink-focusable',
    'argink-motion-safe',
    className,
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    isLoading = false,
    fullWidth = false,
    disabled,
    className,
    children,
    type,
    ...rest
  },
  ref,
) {
  const effectiveSize = variant === 'accent' && size !== 'lg' ? 'lg' : size;
  if (variant === 'accent' && size !== 'lg') {
    devWarn(
      'Button variant="accent" (fundo Trust Blue) só cumpre contraste AA em texto grande — ' +
        'size foi forçado para "lg". Use variant="primary" ou "outline" se precisar de um ' +
        'botão pequeno (BRAND-EXTRACT §5).',
    );
  }

  const isDisabled = Boolean(disabled) || isLoading;

  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={buttonClassName({ variant, size: effectiveSize, fullWidth, className })}
      disabled={isDisabled}
      aria-busy={isLoading || undefined}
      {...rest}
    >
      {isLoading ? (
        <span className="argink-btn__spinner" aria-hidden="true" data-testid="argink-btn-spinner" />
      ) : null}
      <span className="argink-btn__label">{children}</span>
      {isLoading ? <span className="argink-sr-only">Carregando</span> : null}
    </button>
  );
});
